const INSTAGRAM_FETCH_URL = "https://stamped.io/api/widget/reviews/batch";
const IMAGE_WIDTH = 200;
const IMAGE_HEIGHT = 250;
const MEDIA_WIDTH = 400;
const MEDIA_HEIGHT = 400;

class InstagramFeed extends HTMLElement {
  constructor() {
    super();
    this.config = this.getAttribute('config') ? JSON.parse(this.getAttribute('config')) : {};
    this.sliderTemplate = this.querySelector('template[id^="Template-instagram-slider-"]');
    this.postTemplate = this.querySelector('template[id^="Template-instagram-post-"]');
  }

  data = undefined;

  async connectedCallback() {
    await this.fetchData();
    this.render();
  }

  disconnectedCallback() { }

  async fetchData() {
    return fetch(INSTAGRAM_FETCH_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ data: [this.config] })
    })
      .then(response => response.json())
      .then(data => {
        this.data = data;
      });
  }

  render() {
    if (!this.data || !this.data[0] || !this.data[0].data || this.data[0].total < 1) return;
    const posts = this.data[0].data;
    const templateHtml = document.createElement('div');
    templateHtml.innerHTML = this.data[0].template;

    const slider = this.sliderTemplate.content.cloneNode(true);
    const htmlPosts = posts.map((post, index) => this.createPost(post, index));

    slider.querySelector('.splide__list').append(...htmlPosts);
    this.append(slider);
  }

  createPost(post, index) {
    const postTemplate = this.postTemplate.content.cloneNode(true);
    this.setAttributeWithIndex(postTemplate.firstElementChild, 'id', index);

    const modalOpener = postTemplate.querySelector('modal-opener');
    this.setAttributeWithIndex(modalOpener, 'data-modal', index);

    const modalDialog = postTemplate.querySelector('modal-dialog');
    this.setAttributeWithIndex(modalDialog, 'id', index);

    const modalClose = postTemplate.querySelector('[id^="ModalClose-"]');
    this.setAttributeWithIndex(modalClose, 'id', index);

    const modalMedia = modalDialog.querySelector('[data-modal-media]');

    this.setTextContent(modalDialog, '[data-modal-username]', post.username);
    this.setTextContent(modalDialog, '[data-modal-caption]', post.mediaText);
    this.setTextContent(modalDialog, '[data-modal-likes]', post.countLikes);
    this.setTextContent(modalDialog, '[data-modal-comments]', post.countComments);

    const products = modalDialog.querySelector('[data-modal-products]');
    post.tags.forEach((tag, tagIndex) => this.createProduct(tag, tagIndex, products, modalMedia));

    if (post.videoUrl) {
      this.createVideoPost(post, postTemplate, modalMedia, modalDialog);
    } else {
      this.createImagePost(post, postTemplate, modalMedia);
    }

    return postTemplate;
  }

  setAttributeWithIndex(element, attribute, index) {
    element.setAttribute(attribute, element.getAttribute(attribute).replace('[index]', index + 1));
  }

  setTextContent(parent, selector, text) {
    const element = parent.querySelector(selector);
    element.textContent = text;
  }

  createProduct(tag, index, products, modalMedia) {
    const { imageUrl, link, text, title } = tag;
    const wrapper = this.createElementWithClasses('div', ['grid__item']);

    const productWrapper = this.createElementWithClasses('a', ['instagram-post__product']);
    productWrapper.setAttribute('data-product-tag', index + 1);
    productWrapper.href = link;

    this.addHoverListeners(productWrapper, index, modalMedia);

    const productTitle = this.createElementWithClasses('div', ['instagram-post__product-title']);
    productTitle.textContent = title;

    const productImage = this.createImageElement(imageUrl, text, IMAGE_WIDTH, IMAGE_HEIGHT);

    productWrapper.append(productImage, productTitle);
    wrapper.append(productWrapper);
    products.append(wrapper);
  }

  addHoverListeners(element, index, modalMedia) {
    element.addEventListener('mouseenter', () => {
      const tag = modalMedia.querySelector(`[data-product-tag="${index + 1}"]`);
      if (tag) tag.classList.add('is-active');
    });

    element.addEventListener('mouseleave', () => {
      const tag = modalMedia.querySelector(`[data-product-tag="${index + 1}"]`);
      if (tag) tag.classList.remove('is-active');
    });
  }

  createElementWithClasses(elementType, classNames) {
    const element = document.createElement(elementType);
    element.classList.add(...classNames);
    return element;
  }

  createImageElement(src, alt, width, height, loading = 'lazy') {
    const img = document.createElement('img');
    img.src = src;
    img.alt = alt;
    img.loading = loading;
    img.width = width;
    img.height = height;
    return img;
  }

  createVideoPost(post, postTemplate, modalMedia, modalDialog) {
    const postVideo = this.createVideo(post);
    postTemplate.querySelector('.instagram-post__thumbnail').prepend(postVideo);

    const deferredMedia = this.createElementWithClasses('deferred-media', ['modal-video__video', 'template-popup']);
    const template = document.createElement('template');
    template.innerHTML = postVideo.outerHTML;
    template.content.querySelector('video').autoplay = true;
    deferredMedia.append(template);
    modalMedia.append(deferredMedia);

    // Play video if not playing
    modalDialog.addEventListener('modal:opened', () => {
      const video = modalMedia.querySelector('video');
      if (video && video.paused) video.play();
    });

    // Resume and pause video
    modalDialog.addEventListener('modal:closed', () => {
      const video = modalMedia.querySelector('video');
      if (video && !video.paused) video.pause();
      video.currentTime = 0;
    });
  }

  createImagePost(post, postTemplate, modalMedia) {
    const postImage = this.createImageElement(post.imageThumbnailUrl, post.mediaText, MEDIA_WIDTH, MEDIA_HEIGHT, 'eager');
    postTemplate.querySelector('.instagram-post__thumbnail').prepend(postImage);
    modalMedia.append(postImage.cloneNode(true));

    post.tags.forEach((tag, index) => {
      const { x, y } = tag;
      const tagDiv = this.createElementWithClasses('div', ['instagram-post__tag']);
      tagDiv.setAttribute('data-product-tag', index + 1);
      tagDiv.style.top = `${y}%`;
      tagDiv.style.left = `${x}%`;
      tagDiv.textContent = index + 1;
      modalMedia.append(tagDiv);
    });
  }

  createVideo(post) {
    const postVideo = document.createElement('video');
    postVideo.src = post.videoUrl;
    postVideo.alt = post.mediaText;
    postVideo.autoplay = false;
    postVideo.controls = false;
    postVideo.muted = true;
    postVideo.loop = true;
    postVideo.preload = 'metadata';
    postVideo.type = 'video/mp4';
    postVideo.playsinline = true;
    postVideo.width = MEDIA_WIDTH;
    postVideo.height = MEDIA_HEIGHT;
    postVideo.setAttribute('always-on', 'true');
    return postVideo;
  }
}

customElements.define('instagram-feed', InstagramFeed);